import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import ConfirmModal from '../../components/modals/ConfirmModal';
import SuccessModal from '../../components/modals/SuccessModal';
import ErrorModal from '../../components/modals/ErrorModal';

const AdminInvoicesPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);  // Changed to array for multi-selection
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showSendToUserModal, setShowSendToUserModal] = useState(false);
  const [emailAddresses, setEmailAddresses] = useState('');  // For comma-separated emails
  const [selectedUserEmails, setSelectedUserEmails] = useState([]);  // For send to user modal
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Organization data for invoices
  const [organizationData, setOrganizationData] = useState(null);
  
  // Date filter states - Single week selection (matches payments page)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedWeek, setSelectedWeek] = useState(getWeekNumber(new Date()));
  
  // Calendar state (single week selection)
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  
  // Invoice preview modal
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);

  // Custom modal states
  const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
  const [successModal, setSuccessModal] = useState({ show: false, message: '' });
  const [errorModal, setErrorModal] = useState({ show: false, message: '', secondaryAction: null });

  // Helper function to get week number (matches payments page exactly)
  function getWeekNumber(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  }

  // Helper function to get week dates (matches payments page exactly)
  function getWeekDates(year, weekNum) {
    // Use UTC dates to match getWeekNumber logic exactly
    const jan1 = new Date(Date.UTC(year, 0, 1));
    const jan1Weekday = jan1.getUTCDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    
    // Convert Sunday (0) to 7 for ISO week calculation
    const jan1Day = jan1Weekday === 0 ? 7 : jan1Weekday;
    
    // Find first Thursday of the year (ISO week 1 contains Jan 4)
    const firstThursday = new Date(Date.UTC(year, 0, 1));
    firstThursday.setUTCDate(1 + (4 - jan1Day + 7) % 7);
    
    // Find the Monday of the first week
    const firstMonday = new Date(firstThursday);
    firstMonday.setUTCDate(firstThursday.getUTCDate() - 3);
    
    // Calculate the start of the requested week
    const weekStart = new Date(firstMonday);
    weekStart.setUTCDate(firstMonday.getUTCDate() + (weekNum - 1) * 7);
    
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setUTCDate(weekStart.getUTCDate() + i);
      dates.push({ date: date.toISOString().split('T')[0], day: date.getUTCDate() });
    }
    return dates;
  }

  // Helper function to format week display (matches payments page exactly)
  const formatWeekDisplay = (year, weekNum) => {
    const weekDates = getWeekDates(year, weekNum);
    const startDate = new Date(weekDates[0].date);
    const endDate = new Date(weekDates[6].date);
    
    const start = startDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
    const end = endDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
    
    return `Week ${weekNum} (${start} - ${end})`;
  };

  // Calendar generation helper
  const generateCalendar = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    
    // Adjust to Monday start
    const dayOfWeek = firstDay.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startDate.setDate(firstDay.getDate() - daysToSubtract);
    
    const weeks = [];
    let currentWeek = [];
    
    for (let i = 0; i < 42; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      
      currentWeek.push(currentDate);
      
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }
    
    return { weeks, month, year };
  };

  // Initialize page and fetch data
  useEffect(() => {
    const loadInitialData = async () => {
      await fetchUsers();
      await fetchOrganizationData();
      setIsLoading(false);
    };
    
    loadInitialData();
  }, []);

  // Fetch invoices when filters change
  useEffect(() => {
    if (selectedUsers.length > 0) {
      fetchInvoices();
    } else {
      setInvoices([]);
    }
  }, [selectedUsers, selectedYear, selectedWeek]);

  // Fetch organization data
  const fetchOrganizationData = async () => {
    try {
      const response = await axios.get(`${API}/admin/organisation`);
      if (response.data) {
        const companyNumber = response.data.company_number || '';
        setOrganizationData({
          name: response.data.name || '',
          address: response.data.address || '',
          registrationNumber: companyNumber ? `Company No: ${companyNumber}` : '',
          phone: response.data.phone || '',
          email: response.data.email || ''
        });
      } else {
        setOrganizationData({
          name: '',
          address: '',
          registrationNumber: '',
          phone: '',
          email: ''
        });
      }
    } catch (error) {
      console.error('Organization data fetch error:', error);
      setOrganizationData({
        name: '',
        address: '',
        registrationNumber: '',
        phone: '',
        email: ''
      });
    }
  };

  // Fetch active CIS registered users
  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      // Filter for active users who are CIS registered
      const cisUsers = response.data.filter(user => 
        user.active && user.cis_registered
      );
      setUsers(cisUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  };

  // Fetch invoices for selected users and week
  const fetchInvoices = async () => {
    if (selectedUsers.length === 0) {
      setInvoices([]);
      return;
    }

    try {
      const weekDates = getWeekDates(selectedYear, selectedWeek);
      const startDate = weekDates[0].date;
      const endDate = weekDates[6].date;
      
      // Fetch invoices for all selected users
      const allInvoices = [];
      for (const userId of selectedUsers) {
        const response = await axios.get(`${API}/admin/invoices`, {
          params: {
            user_id: userId,
            start_date: startDate,
            end_date: endDate
          }
        });
        
        const invoiceData = response.data || [];
        
        // Add user info to each invoice for display
        const user = users.find(u => u.id === userId);
        const invoicesWithUser = invoiceData.map(inv => ({
          ...inv,
          user_name: user ? `${user.first_name} ${user.last_name}` : 'Unknown'
        }));
        
        // If no invoice data, create placeholder
        if (invoicesWithUser.length === 0) {
          invoicesWithUser.push({
            id: `placeholder_${userId}_${startDate}_${endDate}`,
            user_id: userId,
            user_name: user ? `${user.first_name} ${user.last_name}` : 'Unknown',
            week_number: selectedWeek,
            year: selectedYear,
            week_start: startDate,
            week_end: endDate,
            regular_hours: 0,
            overtime_hours: 0,
            regular_pay: 0,
            overtime_pay: 0,
            gross_total: 0,
            expenses: 0,
            cis_deduction: 0,
            total_amount: 0,
            sites_worked: "",
            payment_status: "no_data",
            created_at: new Date().toISOString(),
            is_placeholder: true
          });
        }
        
        allInvoices.push(...invoicesWithUser);
      }
      
      setInvoices(allInvoices);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setInvoices([]);
    }
  };

  // Handle user toggle for multi-selection
  const handleUserToggle = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Handle select all users
  const handleSelectAllUsers = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.id));
    }
  };

  // Handle date click in calendar (single week selection)
  const handleDateClick = (date) => {
    const year = date.getFullYear();
    const week = getWeekNumber(date);
    setSelectedYear(year);
    setSelectedWeek(week);
    setShowCalendar(false);
  };

  // Go to current week
  const goToCurrentWeek = () => {
    const today = new Date();
    const currentWeekNum = getWeekNumber(today);
    const currentYearNum = today.getFullYear();
    setSelectedYear(currentYearNum);
    setSelectedWeek(currentWeekNum);
  };

  // Check if currently viewing the current week
  const isCurrentWeekSelected = () => {
    const today = new Date();
    const currentWeekNum = getWeekNumber(today);
    const currentYearNum = today.getFullYear();
    return selectedYear === currentYearNum && selectedWeek === currentWeekNum;
  };

  // Navigate to previous week
  const goToPreviousWeek = () => {
    const weekDates = getWeekDates(selectedYear, selectedWeek);
    const currentMonday = new Date(weekDates[0].date);
    currentMonday.setDate(currentMonday.getDate() - 7);
    
    const newWeekNum = getWeekNumber(currentMonday);
    const newYear = currentMonday.getFullYear();
    
    // Handle edge case where the date might belong to a different ISO year
    if (newWeekNum > 50 && currentMonday.getMonth() === 0) {
      setSelectedYear(newYear - 1);
    } else if (newWeekNum === 1 && currentMonday.getMonth() === 11) {
      setSelectedYear(newYear + 1);
    } else {
      setSelectedYear(newYear);
    }
    setSelectedWeek(newWeekNum);
  };

  // Navigate to next week
  const goToNextWeek = () => {
    const weekDates = getWeekDates(selectedYear, selectedWeek);
    const currentMonday = new Date(weekDates[0].date);
    currentMonday.setDate(currentMonday.getDate() + 7);
    
    const newWeekNum = getWeekNumber(currentMonday);
    const newYear = currentMonday.getFullYear();
    
    // Handle edge case where the date might belong to a different ISO year
    if (newWeekNum > 50 && currentMonday.getMonth() === 0) {
      setSelectedYear(newYear - 1);
    } else if (newWeekNum === 1 && currentMonday.getMonth() === 11) {
      setSelectedYear(newYear + 1);
    } else {
      setSelectedYear(newYear);
    }
    setSelectedWeek(newWeekNum);
  };

  // Handle invoice selection
  const handleInvoiceSelect = (invoiceId) => {
    const newSelected = new Set(selectedInvoices);
    if (newSelected.has(invoiceId)) {
      newSelected.delete(invoiceId);
    } else {
      newSelected.add(invoiceId);
    }
    setSelectedInvoices(newSelected);
  };

  // Handle select all invoices
  const handleSelectAll = () => {
    const selectableInvoices = invoices.filter(inv => !inv.is_placeholder);
    if (selectedInvoices.size === selectableInvoices.length && selectableInvoices.length > 0) {
      setSelectedInvoices(new Set());
    } else {
      setSelectedInvoices(new Set(selectableInvoices.map(inv => inv.id)));
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedInvoices(new Set());
  };

  // Download invoices. Pass `idsOverride` (array of canonical invoice IDs)
  // when retrying a partial-failure batch — otherwise the current
  // selection is used.
  const handleDownloadSelected = async (idsOverride = null) => {
    const targetIds = idsOverride
      ?? invoices.filter(inv => selectedInvoices.has(inv.id)).map(inv => inv.id);
    
    if (!targetIds || targetIds.length === 0) {
      setErrorModal({
        show: true,
        message: 'Please select at least one statement to download.'
      });
      return;
    }
    const isRetry = idsOverride !== null;

    setIsProcessing(true);
    try {
      const response = await axios.post(`${API}/admin/invoices/download`, {
        invoices: targetIds
      }, {
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const weekDates = getWeekDates(selectedYear, selectedWeek);
      const filenameSuffix = isRetry ? '_retry' : '';
      const filename = `CIS_Statements_Week${selectedWeek}_${selectedYear}_${weekDates[0].date}${filenameSuffix}.zip`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Read partial-failure counters from response headers — backend
      // sets X-Invoices-Succeeded / X-Invoices-Failed when PDFs are
      // individually generated. Defaults to the requested count when
      // headers are missing (legacy backend or unexpected proxy stripping).
      // X-Invoices-Failed-Ids carries the exact IDs that failed so we
      // can offer a one-click "Retry failed only" CTA on the error modal.
      const requested = targetIds.length;
      const succeeded = parseInt(response.headers['x-invoices-succeeded'] ?? String(requested), 10);
      const failed = parseInt(response.headers['x-invoices-failed'] ?? '0', 10);
      const failedIdsHeader = response.headers['x-invoices-failed-ids'] ?? '';
      const failedIdsTruncated = response.headers['x-invoices-failed-ids-truncated'] === 'true';
      const failedIds = failedIdsHeader
        ? failedIdsHeader.split(',').map(s => s.trim()).filter(Boolean)
        : [];
      
      // Only clear selection on the original download. On a retry we
      // keep the selection intact in case the admin wants to take
      // another action afterwards.
      if (!isRetry) clearSelection();
      
      if (failed > 0) {
        const baseMsg = `Downloaded ${succeeded} of ${requested} statement${requested === 1 ? '' : 's'}. ${failed} failed — check network/PDF service and try again for the missing ones.`;
        // Offer "Retry failed only" if we know which IDs failed AND
        // the count is sane (truncated header means we got the count
        // but not the IDs — fall back gracefully).
        const canRetry = failedIds.length > 0 && !failedIdsTruncated;
        setErrorModal({
          show: true,
          message: canRetry
            ? baseMsg
            : baseMsg + (failedIdsTruncated ? ' (Too many failures to list individually — please re-select and try again.)' : ''),
          secondaryAction: canRetry ? {
            label: `Retry ${failedIds.length} failed`,
            onClick: () => {
              setErrorModal({ show: false, message: '' });
              // Defer to next tick so the modal close animation fires
              // before the new download starts.
              setTimeout(() => handleDownloadSelected(failedIds), 50);
            }
          } : null,
        });
      } else {
        setSuccessModal({
          show: true,
          message: isRetry
            ? `Retry successful — downloaded ${succeeded} statement${succeeded === 1 ? '' : 's'}.`
            : `Successfully downloaded ${succeeded} statement${succeeded === 1 ? '' : 's'}.`
        });
      }
    } catch (error) {
      console.error('Error downloading invoices:', error);
      setErrorModal({
        show: true,
        message: 'Failed to download statements. Please try again.'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Send invoices to user
  const handleSendToUser = async () => {
    if (selectedInvoices.size === 0) {
      setErrorModal({
        show: true,
        message: 'Please select at least one statement to send.'
      });
      return;
    }

    // Get unique users from selected invoices
    const selectedInvoiceData = invoices.filter(inv => selectedInvoices.has(inv.id));
    const uniqueUserIds = [...new Set(selectedInvoiceData.map(inv => inv.user_id))];
    const selectedUsersData = uniqueUserIds.map(userId => {
      const user = users.find(u => u.id === userId);
      const userInvoices = selectedInvoiceData.filter(inv => inv.user_id === userId);
      return {
        id: userId,
        name: user ? `${user.first_name} ${user.last_name}` : 'Unknown',
        email: user?.email || '',
        invoiceCount: userInvoices.length,
        invoiceIds: userInvoices.map(inv => inv.id)
      };
    });

    // Check if all users have emails
    const usersWithoutEmail = selectedUsersData.filter(u => !u.email);
    if (usersWithoutEmail.length > 0) {
      setErrorModal({
        show: true,
        message: `The following user(s) don't have email addresses: ${usersWithoutEmail.map(u => u.name).join(', ')}. Please update their profiles.`
      });
      return;
    }

    setSelectedUserEmails(selectedUsersData);
    setShowSendToUserModal(true);
  };

  // Confirm send to user
  const confirmSendToUser = async () => {
    setIsProcessing(true);
    try {
      // Send statements to each user
      for (const userData of selectedUserEmails) {
        await axios.post(`${API}/admin/invoices/send-to-user`, {
          invoices: userData.invoiceIds,
          user_id: userData.id
        });
      }

      setShowSendToUserModal(false);
      setSelectedUserEmails([]);
      clearSelection();
      setSuccessModal({
        show: true,
        message: `Successfully sent statements to ${selectedUserEmails.length} user(s).`
      });
    } catch (error) {
      console.error('Error sending invoices to users:', error);
      setErrorModal({
        show: true,
        message: error.response?.data?.detail || 'Failed to send statements. Please try again.'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Send invoices by email
  const handleSendByEmail = () => {
    if (selectedInvoices.size === 0) {
      setErrorModal({
        show: true,
        message: 'Please select at least one statement to send.'
      });
      return;
    }
    setShowEmailModal(true);
  };

  // Confirm send by email (forward to external emails)
  const confirmSendByEmail = async () => {
    const emails = emailAddresses.split(',').map(e => e.trim()).filter(e => e);
    
    if (emails.length === 0) {
      setErrorModal({
        show: true,
        message: 'Please enter at least one email address.'
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter(e => !emailRegex.test(e));
    if (invalidEmails.length > 0) {
      setErrorModal({
        show: true,
        message: `Invalid email address(es): ${invalidEmails.join(', ')}`
      });
      return;
    }

    setIsProcessing(true);
    try {
      const selectedInvoiceData = invoices.filter(inv => selectedInvoices.has(inv.id));
      
      await axios.post(`${API}/admin/invoices/send-by-email`, {
        invoices: selectedInvoiceData.map(inv => inv.id),
        emails: emails
      });

      setShowEmailModal(false);
      setEmailAddresses('');
      clearSelection();
      setSuccessModal({
        show: true,
        message: `Successfully sent ${selectedInvoices.size} statement(s) to ${emails.length} recipient(s).`
      });
    } catch (error) {
      console.error('Error sending invoices by email:', error);
      setErrorModal({
        show: true,
        message: error.response?.data?.detail || 'Failed to send statements. Please try again.'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Preview invoice
  const handlePreviewInvoice = async (invoice) => {
    if (invoice.is_placeholder) {
      setErrorModal({
        show: true,
        message: 'No payment data available for this week.'
      });
      return;
    }
    
    setPreviewInvoice(invoice);
    
    // Generate PDF and create object URL for modal display
    try {
      const pdfUrl = await generatePreviewPDF(invoice, true); // true for modal display
      setPreviewPdfUrl(pdfUrl);
      setShowPreviewModal(true);
    } catch (error) {
      console.error('Error generating PDF preview:', error);
      setErrorModal({
        show: true,
        message: 'Error generating statement preview. Please try again.'
      });
    }
  };

  // Generate preview PDF (similar to payments page)
  const generatePreviewPDF = async (invoice, forModal = false) => {
    if (!organizationData) return;

    // Dynamically import jsPDF
    const { jsPDF } = await import('jspdf');
    const html2canvas = (await import('html2canvas')).default;

    const user = users.find(u => u.id === invoice.user_id);
    if (!user) return;

    // Create temporary div for invoice preview - A4 dimensions (210mm ≈ 794px at 96dpi)
    const invoiceDiv = document.createElement('div');
    invoiceDiv.style.cssText = 'position: absolute; left: -9999px; width: 210mm; min-height: 297mm; background: white; padding: 12mm 15mm; font-family: Arial, sans-serif; box-sizing: border-box;';
    
    // Work Period calculation
    const workPeriod = `${invoice.week_start} to ${invoice.week_end}`;
    
    // Format payment date - use actual payment_date from invoice, fallback to empty if not available
    let paymentDate = '';
    if (invoice.payment_status === 'paid' && invoice.payment_date) {
      // payment_date comes as YYYY-MM-DD string from backend
      const dateObj = new Date(invoice.payment_date);
      paymentDate = dateObj.toLocaleDateString('en-GB');
    }
    
    // Calculate consistent hourly rate
    const hourlyRate = (invoice.regular_pay / invoice.regular_hours || 15.50).toFixed(2);

    invoiceDiv.innerHTML = `
      <div style="width: 100%; background: #ffffff; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif; color: #1a1a1a; line-height: 1.3;">
        
        <!-- Professional Header -->
        <div style="background: #ffffff; padding: 12px 20px 10px 20px; border-bottom: 2px solid #e5e7eb;">
          <div style="display: flex; justify-content: space-between; align-items: flex-start;">
            <div>
              <h1 style="font-size: 32px; font-weight: 300; margin: 0; color: #111827; letter-spacing: -1px; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">CIS STATEMENT</h1>
            </div>
            <div style="display: flex; gap: 6px; align-items: center; margin-top: 18px;">
              ${invoice.payment_status === 'pending' ? `
              <div style="background: #fef3c7; color: #92400e; padding: 4px 10px 12px 10px; border-radius: 3px; font-weight: 500; font-size: 10px; border: 1px solid #fcd34d; display: flex; align-items: center; justify-content: center; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">
                PENDING
              </div>
              ` : `
              <div style="background: #d1fae5; color: #065f46; padding: 4px 10px 12px 10px; border-radius: 3px; font-weight: 500; font-size: 10px; border: 1px solid #86efac; display: flex; align-items: center; justify-content: center; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">
                PAID (${paymentDate})
              </div>
              `}
            </div>
          </div>
        </div>

        <!-- Two-Column Information Grid -->
        <div style="display: grid; grid-template-columns: 1fr 1fr; grid-template-rows: auto auto; gap: 24px; padding: 16px 20px;">
          
          <!-- Top Left: Contractor Information -->
          <div style="grid-column: 1; grid-row: 1;">
            <div style="margin-bottom: 22px; min-height: 120px;">
              <h3 style="font-size: 12px; font-weight: 600; color: #ffffff; text-transform: uppercase; letter-spacing: 0.6px; margin: 0 0 12px 0; padding: 8px 12px 12px 12px; background: linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%); border-radius: 4px; display: flex; align-items: center; justify-content: flex-start; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">Sub-Contractor Details</h3>
              <div style="padding-left: 10px;">
                <div style="margin-bottom: 10px;">
                  <span style="color: #111827; display: block; margin-bottom: 2px; font-size: 12px; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">${user.first_name} ${user.last_name}</span>
                  <span style="color: #6b7280; font-size: 12px; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">${user.role || 'Employee'}</span>
                </div>
                
                <div style="margin-bottom: 10px;">
                  <span style="font-size: 12px; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.3px; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">Address</span>
                  <p style="margin: 2px 0 0 0; color: #374151; font-size: 12px; line-height: 1.3; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">${user.address || 'Not provided'}</p>
                </div>
                
                ${user.ni_number ? `
                <div style="margin-bottom: 10px;">
                  <span style="font-size: 12px; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.3px; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">NI Number</span>
                  <p style="margin: 2px 0 0 0; color: #374151; font-size: 12px; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">${user.ni_number}</p>
                </div>
                ` : ''}
                
                ${user.cis_registered && user.utr_number ? `
                <div style="margin-bottom: 10px;">
                  <span style="font-size: 12px; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.3px; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">UTR Number</span>
                  <p style="margin: 2px 0 0 0; color: #374151; font-size: 12px; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">${user.utr_number}</p>
                </div>
                ` : ''}
              </div>
            </div>
          </div>

          <!-- Top Right: Company Information -->
          <div style="grid-column: 2; grid-row: 1;">
            <div style="margin-bottom: 22px; min-height: 120px;">
              <h3 style="font-size: 12px; font-weight: 600; color: #ffffff; text-transform: uppercase; letter-spacing: 0.6px; margin: 0 0 12px 0; padding: 8px 12px 12px 12px; background: linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%); border-radius: 4px; display: flex; align-items: center; justify-content: flex-start; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">Company Details</h3>
              <div style="padding-left: 10px;">
                <div style="margin-bottom: 10px;">
                  <span style="color: #111827; display: block; margin-bottom: 2px; font-size: 12px; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">${organizationData.name}</span>
                </div>
                
                <div style="margin-bottom: 10px;">
                  <p style="margin: 2px 0 0 0; color: #374151; font-size: 12px; line-height: 1.3; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">${organizationData.address}</p>
                </div>
                
                <div style="margin-bottom: 10px;">
                  <p style="margin: 2px 0 0 0; color: #374151; font-size: 12px; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">${organizationData.phone}</p>
                </div>
                
                <div style="margin-bottom: 10px;">
                  <p style="margin: 2px 0 0 0; color: #374151; font-size: 12px; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">${organizationData.registrationNumber}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Bottom Left: Work Details -->
          <div style="grid-column: 1; grid-row: 2;">
            <h3 style="font-size: 12px; font-weight: 600; color: #ffffff; text-transform: uppercase; letter-spacing: 0.6px; margin: 0 0 12px 0; padding: 8px 12px 12px 12px; background: linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%); border-radius: 4px; display: flex; align-items: center; justify-content: flex-start; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">Work Details</h3>
            
            <div style="padding-left: 10px;">
              <div style="margin-bottom: 10px;">
                <span style="font-size: 12px; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.3px; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">Site(s)</span>
                <p style="margin: 2px 0 0 0; color: #374151; font-size: 12px; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">${invoice.sites_worked && invoice.sites_worked.length > 0 ? invoice.sites_worked : 'No sites assigned'}</p>
              </div>
              
              <div>
                <span style="font-size: 12px; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.3px; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">Work Period</span>
                <p style="margin: 2px 0 0 0; color: #111827; font-size: 12px; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">${workPeriod}</p>
              </div>
            </div>
          </div>

          <!-- Bottom Right: Payment Details -->
          <div style="grid-column: 2; grid-row: 2;">
            <h3 style="font-size: 12px; font-weight: 600; color: #ffffff; text-transform: uppercase; letter-spacing: 0.6px; margin: 0 0 12px 0; padding: 8px 12px 12px 12px; background: linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%); border-radius: 4px; display: flex; align-items: center; justify-content: flex-start; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">Payment Details</h3>
            
            <div style="padding-left: 10px;">
              <div style="margin-bottom: 10px;">
                <span style="font-size: 12px; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.3px; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">Bank Name</span>
                <p style="margin: 2px 0 0 0; color: #374151; font-size: 12px; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">${user.bank_name || 'Not provided'}</p>
              </div>
              
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
                <div>
                  <span style="font-size: 12px; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.3px; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">Account</span>
                  <p style="margin: 2px 0 0 0; color: #374151; font-size: 12px; font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;">${user.account_number ? '****' + user.account_number.slice(-4) : 'Not provided'}</p>
                </div>
                <div>
                  <span style="font-size: 12px; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.3px; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">Sort Code</span>
                  <p style="margin: 2px 0 0 0; color: #374151; font-size: 12px; font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;">${user.sort_code ? user.sort_code.replace(/(.{2})/g, '$1-').slice(0, -1) : 'Not provided'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Earnings Breakdown Section -->
        <div style="background: #f9fafb; border-top: 1px solid #e5e7eb; padding: 22px 28px;">
          
          <h2 style="font-size: 12px; font-weight: 600; color: #ffffff; text-transform: uppercase; letter-spacing: 0.6px; margin: 0 0 18px 0; padding: 8px 12px 12px 12px; background: linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%); border-radius: 4px; display: flex; align-items: center; justify-content: flex-start; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">Earnings Breakdown</h2>

          <!-- Hours Summary Cards -->
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 18px;">
            
            <!-- Regular Hours -->
            <div style="background: #ffffff; border: 1px solid #e5e7eb; border-radius: 5px; padding: 12px;">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <div>
                  <h4 style="font-size: 12px; font-weight: 600; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.3px; margin: 0 0 4px 0; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">Regular Hours</h4>
                  <p style="margin: 0; font-size: 12px; color: #374151; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">${invoice.regular_hours}h × £${hourlyRate}/hr</p>
                </div>
                <div style="text-align: right;">
                  <p style="margin: 0; font-size: 12px; font-weight: 500; color: #111827; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">£${invoice.regular_pay.toFixed(2)}</p>
                </div>
              </div>
            </div>

            <!-- Overtime Hours -->
            <div style="background: #ffffff; border: 1px solid #e5e7eb; border-radius: 5px; padding: 12px;">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <div>
                  <h4 style="font-size: 12px; font-weight: 600; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.3px; margin: 0 0 4px 0; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">Overtime Hours</h4>
                  <p style="margin: 0; font-size: 12px; color: #374151; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">${invoice.overtime_hours}h × £${hourlyRate}/hr</p>
                </div>
                <div style="text-align: right;">
                  <p style="margin: 0; font-size: 12px; font-weight: 500; color: #111827; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">£${invoice.overtime_pay.toFixed(2)}</p>
                </div>
              </div>
            </div>

          </div>

          <!-- Payment Summary -->
          <div style="background: #ffffff; border: 1px solid #e5e7eb; border-radius: 6px; overflow: hidden; box-shadow: 0 2px 3px -1px rgba(0, 0, 0, 0.05); margin-bottom: 18px;">
            
            <!-- Gross Earnings -->
            <div style="padding: 12px; border-bottom: 1px solid #f3f4f6;">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <span style="font-size: 12px; color: #374151; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">Gross Earnings</span>
                <span style="font-size: 12px; color: #111827; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">£${invoice.gross_total.toFixed(2)}</span>
              </div>
            </div>

            ${user.cis_registered ? `
            <!-- CIS Deduction -->
            <div style="padding: 12px; border-bottom: 1px solid #f3f4f6; background: #fefefe;">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <div>
                  <span style="font-size: 12px; color: #111827; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">CIS Tax Deduction</span>
                  <span style="font-size: 12px; color: #9ca3af; margin-left: 4px; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">(20%)</span>
                </div>
                <span style="font-size: 12px; color: #111827; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">-£${invoice.cis_deduction.toFixed(2)}</span>
              </div>
            </div>
            
            <!-- Net Earnings -->
            <div style="padding: 12px; border-bottom: 1px solid #f3f4f6;">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <span style="font-size: 12px; color: #111827; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">Net Earnings</span>
                <span style="font-size: 12px; color: #111827; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">£${(invoice.gross_total - invoice.cis_deduction).toFixed(2)}</span>
              </div>
            </div>
            ` : `
            <!-- Net Earnings (No CIS) -->
            <div style="padding: 12px; border-bottom: 1px solid #f3f4f6;">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <span style="font-size: 12px; color: #111827; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">Net Earnings</span>
                <span style="font-size: 12px; color: #111827; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">£${invoice.gross_total.toFixed(2)}</span>
              </div>
            </div>
            `}

            <!-- Expenses -->
            <div style="padding: 12px; border-bottom: 1px solid #f3f4f6;">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <span style="font-size: 12px; color: #111827; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">Approved Expenses</span>
                <span style="font-size: 12px; color: #111827; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">£${invoice.expenses.toFixed(2)}</span>
              </div>
            </div>

            <!-- Total Payment Due -->
            <div style="padding: 16px 12px; background: #111827;">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <span style="font-size: 12px; font-weight: 600; color: #ffffff; text-transform: uppercase; letter-spacing: 0.6px; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">Total Payment Due</span>
                <span style="font-size: 12px; font-weight: 700; color: #ffffff; font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;">£${invoice.total_amount.toFixed(2)}</span>
              </div>
            </div>

          </div>
          
        </div>

      </div>
    `;

    document.body.appendChild(invoiceDiv);

    try {
      const canvas = await html2canvas(invoiceDiv, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, imgWidth, imgHeight);
      
      if (forModal) {
        // Return blob URL for modal display
        const pdfBlob = pdf.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);
        return pdfUrl;
      } else {
        // Open preview in new window
        const pdfBlob = pdf.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, '_blank');
        
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
      }
    } catch (error) {
      console.error('Error generating PDF preview:', error);
      setErrorModal({
        show: true,
        message: 'Error generating statement preview. Please try again.'
      });
    } finally {
      document.body.removeChild(invoiceDiv);
    }
  };

  // Calendar Component - Single week selection (matches Payments page)
  const Calendar = ({ currentDate, onDateClick, onClose, selectedYear, selectedWeek }) => {
    const [displayDate, setDisplayDate] = useState(currentDate);
    const { weeks, month, year } = generateCalendar(displayDate);
    const calendarRef = React.useRef(null);
    
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (calendarRef.current && !calendarRef.current.contains(event.target)) {
          onClose();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [onClose]);
    
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const navigateMonth = (direction) => {
      const newDate = new Date(displayDate);
      newDate.setMonth(newDate.getMonth() + direction);
      setDisplayDate(newDate);
    };
    
    const isSelectedWeek = (date) => {
      const weekNum = getWeekNumber(date);
      return date.getFullYear() === selectedYear && weekNum === selectedWeek;
    };
    
    return (
      <div className="relative">
        <div className="absolute z-[9999] mt-2 left-0">
          <div 
            ref={calendarRef}
            className="bg-slate-800 rounded-xl shadow-xl border border-slate-600 p-6 w-80"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Select a Week</h3>
              <button 
                onClick={onClose}
                className="text-gray-400 hover:text-gray-300 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="flex items-center justify-between mb-4">
              <button 
                onClick={() => navigateMonth(-1)}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h4 className="text-lg font-semibold text-white">
                {monthNames[month]} {year}
              </h4>
              <button 
                onClick={() => navigateMonth(1)}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-400 py-2">
                  {day}
                </div>
              ))}
            </div>
            
            <div className="grid grid-cols-7 gap-1">
              {weeks.map((week, weekIndex) => (
                week.map((date, dayIndex) => {
                  const isCurrentMonth = date.getMonth() === month;
                  const isSelected = isSelectedWeek(date);
                  const today = new Date();
                  today.setHours(23, 59, 59, 999);
                  const isFuture = date > today;
                  
                  return (
                    <button
                      key={`${weekIndex}-${dayIndex}`}
                      onClick={() => !isFuture && onDateClick(date)}
                      disabled={isFuture}
                      className={`p-2 text-sm rounded-lg transition-colors ${
                        isCurrentMonth ? 'text-white' : 'text-gray-500'
                      } ${
                        isFuture 
                          ? 'text-gray-600 cursor-not-allowed bg-slate-700/30' 
                          : isSelected 
                            ? 'bg-purple-600 text-white' 
                            : 'hover:bg-slate-700'
                      }`}
                    >
                      {date.getDate()}
                    </button>
                  );
                })
              ))}
            </div>
            
            <div className="mt-4 text-sm text-gray-400">
              Click on any day to select that week
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 -m-8 p-8 flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
        <span className="ml-3 text-gray-400">Loading statements...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 -m-8 p-8 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-6 text-white">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2">CIS Statements</h1>
        <p className="text-purple-200">Download and send statements for CIS registered employees</p>
      </div>

      {/* Bulk Actions Header - Only show if all selected invoices are paid */}
      {selectedInvoices.size > 0 && (() => {
        const selectedInvoiceObjects = invoices.filter(inv => selectedInvoices.has(inv.id));
        const allSelectedArePaid = selectedInvoiceObjects.every(inv => inv.payment_status === 'paid');
        
        return allSelectedArePaid && (
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="text-lg font-semibold text-white">
                  Bulk Actions ({selectedInvoices.size} paid statements selected)
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleDownloadSelected}
                  disabled={isProcessing}
                  className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-lg transition-all duration-200 flex items-center disabled:opacity-50"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download Selected Statements
                </button>
                <button
                  onClick={handleSendToUser}
                  disabled={isProcessing}
                  className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-all duration-200 flex items-center disabled:opacity-50"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                  Send Statements To User
                </button>
                <button
                  onClick={handleSendByEmail}
                  disabled={isProcessing}
                  className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-lg transition-all duration-200 flex items-center disabled:opacity-50"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Forward Statements to Email
                </button>
                <button
                  onClick={clearSelection}
                  className="text-gray-300 hover:text-white px-3 py-2 rounded-lg border border-slate-600 hover:bg-slate-700 transition-colors"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Filters Section */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-4 mb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-white ml-3">Filters & Week Selection</h2>
          </div>
          
          {/* Current Week Button - Only active when not on current week */}
          <button
            onClick={goToCurrentWeek}
            disabled={isCurrentWeekSelected()}
            className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 text-sm flex items-center ${
              isCurrentWeekSelected()
                ? 'bg-slate-700 text-gray-500 cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-500 text-white'
            }`}
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
            </svg>
            Current Week
          </button>
        </div>
        
        {/* Team Member Selection - Pill Style */}
        <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-bold text-gray-300 flex items-center">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Team Members ({selectedUsers.length} selected)
            </h4>
            <button
              onClick={handleSelectAllUsers}
              className="text-xs text-purple-400 hover:text-purple-300 font-medium transition-colors"
            >
              {selectedUsers.length === users.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {users.map(user => (
              <button
                key={user.id}
                onClick={() => handleUserToggle(user.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                  selectedUsers.includes(user.id)
                    ? 'bg-purple-600 text-white shadow-lg'
                    : 'bg-slate-800 text-gray-400 hover:text-white hover:bg-slate-700 border border-slate-600'
                }`}
              >
                {user.first_name} {user.last_name}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Only active, CIS registered team members are shown
          </p>
        </div>

        {/* Week Selection with Navigation */}
        <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600 relative">
          <h4 className="text-xs font-bold text-gray-300 mb-2 flex items-center">
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
            </svg>
            Week Selection
          </h4>
          <div className="flex items-center space-x-2">
            {/* Previous Week Button */}
            <button
              onClick={goToPreviousWeek}
              className="p-2 bg-slate-800 border border-slate-600 rounded-lg hover:bg-slate-700 transition-colors"
            >
              <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            {/* Week Display Button */}
            <button
              onClick={() => setShowCalendar(true)}
              className="flex-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 text-left flex items-center justify-between hover:bg-slate-700"
            >
              <span>{formatWeekDisplay(selectedYear, selectedWeek)}</span>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
              </svg>
            </button>
            
            {/* Next Week Button */}
            <button
              onClick={goToNextWeek}
              disabled={isCurrentWeekSelected()}
              className={`p-2 border rounded-lg transition-colors ${
                isCurrentWeekSelected()
                  ? 'bg-slate-800 border-slate-700 text-gray-600 cursor-not-allowed'
                  : 'bg-slate-800 border-slate-600 hover:bg-slate-700 text-gray-300'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          
          {showCalendar && (
            <Calendar
              currentDate={calendarDate}
              onDateClick={handleDateClick}
              onClose={() => setShowCalendar(false)}
              selectedYear={selectedYear}
              selectedWeek={selectedWeek}
            />
          )}
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="border-b border-slate-700 px-6 py-4">
          <h3 className="text-lg font-semibold text-white">
            {selectedUsers.length > 0 ? 
              `Statements for ${selectedUsers.length} team member${selectedUsers.length > 1 ? 's' : ''} - Week ${selectedWeek}, ${selectedYear}` : 
              'Select team members to view statements'
            }
          </h3>
        </div>

        {selectedUsers.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h3 className="mt-2 text-sm font-medium text-white">No team members selected</h3>
            <p className="mt-1 text-sm text-gray-400">Please select one or more team members from the pills above to view their statements.</p>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            <span className="ml-3 text-gray-400">Loading statements...</span>
          </div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="mt-2 text-sm font-medium text-white">No statements found</h3>
            <p className="mt-1 text-sm text-gray-400">No statements found for the selected date range.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-slate-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider w-12">
                    <input
                      type="checkbox"
                      checked={invoices.length > 0 && invoices.filter(inv => !inv.is_placeholder).every(inv => selectedInvoices.has(inv.id))}
                      ref={input => {
                        if (input) {
                          const selectableInvoices = invoices.filter(inv => !inv.is_placeholder);
                          const someSelected = selectedInvoices.size > 0 && !selectableInvoices.every(inv => selectedInvoices.has(inv.id));
                          input.indeterminate = someSelected;
                        }
                      }}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-purple-600 bg-slate-700 border-slate-500 rounded focus:ring-purple-500 focus:ring-2"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Team Member</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Week</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Period</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Regular Hours</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Overtime Hours</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Gross Earnings</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">CIS Deductions</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Expenses</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Payment Due</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice, index) => (
                  <tr 
                    key={invoice.id} 
                    className={`hover:bg-slate-700/50 transition-colors ${
                      selectedInvoices.has(invoice.id) ? 'bg-purple-900/20' : ''
                    } ${
                      invoice.is_placeholder ? 'bg-slate-800/50 opacity-60' : ''
                    } ${index !== invoices.length - 1 ? 'border-b border-slate-700/50' : ''}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      {!invoice.is_placeholder && (
                        <input
                          type="checkbox"
                          checked={selectedInvoices.has(invoice.id)}
                          onChange={() => handleInvoiceSelect(invoice.id)}
                          className="w-4 h-4 text-purple-600 bg-slate-700 border-slate-500 rounded focus:ring-purple-500 focus:ring-2"
                        />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                      {invoice.user_name || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      Week {invoice.week_number}, {invoice.year}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {new Date(invoice.week_start).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} - {new Date(invoice.week_end).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {invoice.is_placeholder ? (
                        <span className="text-gray-500 italic">No data</span>
                      ) : (
                        `${invoice.regular_hours}h`
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {invoice.is_placeholder ? (
                        <span className="text-gray-500 italic">No data</span>
                      ) : (
                        <span className="text-orange-400">{invoice.overtime_hours}h</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {invoice.is_placeholder ? (
                        <span className="text-gray-500 italic">No data</span>
                      ) : (
                        `£${invoice.gross_total.toFixed(2)}`
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {invoice.is_placeholder ? (
                        <span className="text-gray-500 italic">No data</span>
                      ) : (
                        <span className="text-red-400">-£{invoice.cis_deduction.toFixed(2)}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {invoice.is_placeholder ? (
                        <span className="text-gray-500 italic">No data</span>
                      ) : (
                        <span className="text-green-400">£{invoice.expenses.toFixed(2)}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                      {invoice.is_placeholder ? (
                        <span className="text-gray-500 italic">No data</span>
                      ) : (
                        `£${invoice.total_amount.toFixed(2)}`
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {invoice.is_placeholder ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-slate-600 text-gray-400">
                          No Data
                        </span>
                      ) : (
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          invoice.payment_status === 'paid' 
                            ? 'bg-green-500/20 text-green-300 border border-green-500/30' 
                            : 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
                        }`}>
                          {invoice.payment_status === 'paid' ? 'Paid' : 'Pending'}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      {invoice.payment_status === 'paid' && !invoice.is_placeholder && (
                        <button
                          onClick={() => handlePreviewInvoice(invoice)}
                          className="text-purple-400 hover:text-purple-300 flex items-center transition-colors"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          Preview
                        </button>
                      )}
                      {invoice.is_placeholder && (
                        <span className="text-gray-500 italic text-xs">No payment data available</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* CIS Statement Preview Modal */}
      {showPreviewModal && previewPdfUrl && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50"
        >
          <div className="bg-slate-900 rounded-2xl w-full max-w-4xl h-full max-h-[90vh] mx-4 flex flex-col border border-slate-700">
            <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h3 className="text-xl font-bold text-white">CIS Statement Preview</h3>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  if (previewPdfUrl) {
                    URL.revokeObjectURL(previewPdfUrl);
                    setPreviewPdfUrl(null);
                  }
                }}
                className="text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 p-6 overflow-hidden bg-slate-800">
              <iframe
                src={previewPdfUrl}
                className="w-full h-full border-0 rounded-lg"
                title="CIS Statement Preview"
              />
            </div>

            <div className="bg-slate-800 px-6 py-4 flex justify-end space-x-4 rounded-b-2xl border-t border-slate-700">
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  if (previewPdfUrl) {
                    URL.revokeObjectURL(previewPdfUrl);
                    setPreviewPdfUrl(null);
                  }
                }}
                className="px-6 py-2 text-gray-300 border border-slate-600 rounded-lg hover:bg-slate-700 transition-colors"
              >
                Close
              </button>
              <a
                href={previewPdfUrl}
                download={`invoice_${previewInvoice?.week_number}_${previewInvoice?.year}.pdf`}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-all duration-200"
              >
                Download PDF
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-900 rounded-2xl w-full max-w-md mx-4 border border-slate-700">
            <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h3 className="text-xl font-bold text-white">Forward Statements to Email</h3>
              <button
                onClick={() => {
                  setShowEmailModal(false);
                  setEmailAddresses('');
                }}
                className="text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <p className="text-gray-300 mb-2">
                  Forward <span className="font-semibold text-purple-400">{selectedInvoices.size} selected statement(s)</span> to:
                </p>
                <p className="text-gray-400 text-sm">Recipients will be BCC'd on the email.</p>
              </div>

              <div className="space-y-3">
                <label className="block text-sm text-gray-400 mb-1">Email Addresses (comma separated)</label>
                <textarea
                  value={emailAddresses}
                  onChange={(e) => setEmailAddresses(e.target.value)}
                  placeholder="email1@example.com, email2@example.com..."
                  rows={3}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                />
              </div>

              <div className="flex items-center justify-end space-x-4 pt-6 border-t border-slate-700 mt-6">
                <button
                  onClick={() => {
                    setShowEmailModal(false);
                    setEmailAddresses('');
                  }}
                  className="px-6 py-2 text-gray-300 border border-slate-600 rounded-lg hover:bg-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSendByEmail}
                  disabled={!emailAddresses.trim() || isProcessing}
                  className={`px-6 py-2 rounded-lg transition-all duration-200 ${
                    emailAddresses.trim() && !isProcessing
                      ? 'bg-purple-600 hover:bg-purple-500 text-white'
                      : 'bg-slate-700 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isProcessing ? 'Sending...' : 'Send Statements'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send to User Modal */}
      {showSendToUserModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-900 rounded-2xl w-full max-w-lg mx-4 border border-slate-700">
            <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h3 className="text-xl font-bold text-white">Send Statements to Users</h3>
              <button
                onClick={() => {
                  setShowSendToUserModal(false);
                  setSelectedUserEmails([]);
                }}
                className="text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <p className="text-gray-300 mb-2">
                  The following users will receive their CIS statements:
                </p>
              </div>

              <div className="space-y-3 max-h-64 overflow-y-auto">
                {selectedUserEmails.map((userData, index) => (
                  <div key={index} className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-white font-medium">{userData.name}</p>
                        <p className="text-gray-400 text-sm">{userData.email}</p>
                      </div>
                      <span className="bg-blue-600/20 text-blue-400 text-xs px-2 py-1 rounded">
                        {userData.invoiceCount} statement(s)
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-end space-x-4 pt-6 border-t border-slate-700 mt-6">
                <button
                  onClick={() => {
                    setShowSendToUserModal(false);
                    setSelectedUserEmails([]);
                  }}
                  className="px-6 py-2 text-gray-300 border border-slate-600 rounded-lg hover:bg-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSendToUser}
                  disabled={isProcessing}
                  className={`px-6 py-2 rounded-lg transition-all duration-200 ${
                    !isProcessing
                      ? 'bg-blue-600 hover:bg-blue-500 text-white'
                      : 'bg-slate-700 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isProcessing ? 'Sending...' : `Send to ${selectedUserEmails.length} User(s)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Modals */}
      <ConfirmModal
        isOpen={confirmModal.show}
        message={confirmModal.message}
        onConfirm={() => {
          if (confirmModal.onConfirm) {
            confirmModal.onConfirm();
          }
          setConfirmModal({ show: false, message: '', onConfirm: null });
        }}
        onClose={() => setConfirmModal({ show: false, message: '', onConfirm: null })}
      />

      <SuccessModal
        isOpen={successModal.show}
        message={successModal.message}
        onClose={() => setSuccessModal({ show: false, message: '' })}
      />

      <ErrorModal
        isOpen={errorModal.show}
        message={errorModal.message}
        secondaryAction={errorModal.secondaryAction}
        onClose={() => setErrorModal({ show: false, message: '', secondaryAction: null })}
      />
    </div>
  );
};

export default AdminInvoicesPage;